import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getFinanceElfLoansList } from '../../endpoints/financeElfLoans';
import { ElfLoanListItem } from '../../types/financeElfLoans';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import { SortOrder } from '../../types/table';

const formatAmount = (value: number): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const ElfLoansList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof ElfLoanListItem>('borrower_name');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );

  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  const params = {
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    order_by: String(orderBy),
    order: order || undefined,
  };

  const { data, isLoading } = useApiGet(
    ['finance-elf-loans', rowsPerPage, page, orderBy, order, search],
    () => getFinanceElfLoansList(params),
    {
      enabled: true,
      retry: 1,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof ElfLoanListItem) => {
    if (
      property !== 'borrower_name' &&
      property !== 'borrower_email' &&
      property !== 'amount' &&
      property !== 'imis_id'
    ) {
      return;
    }
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const columns: ServerSideTableColumn<ElfLoanListItem>[] = [
    {
      id: 'borrower_name',
      label: 'Name',
      width: '28%',
      firstCell: true,
      render: (_value, row) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 320,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.35,
            fontWeight: 500,
          }}
        >
          {row.borrower_name?.trim() || '—'}
        </Typography>
      ),
    },
    {
      id: 'borrower_email',
      label: 'Email',
      width: '32%',
      render: (_value, row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: 'break-all' }}
        >
          {row.borrower_email?.trim() || '—'}
        </Typography>
      ),
    },
    {
      id: 'imis_id',
      label: 'iMIS ID',
      width: '18%',
      render: (_value, row) => (
        <Typography variant="body2">{row.imis_id?.trim() || '—'}</Typography>
      ),
    },
    {
      id: 'amount',
      label: 'Amount',
      width: '22%',
      render: (_value, row) => (
        <Typography variant="body2">{formatAmount(row.amount)}</Typography>
      ),
    },
  ];

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Typography variant="h4" color="primary">
          ELF Loans
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ py: 2, maxWidth: 480 }}>
          <TextField
            label="Search by name, email, iMIS, or phone"
            id="elf-loans-search-field"
            size="small"
            value={delaySearch}
            fullWidth
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/finance/elf-loans/${row.id}`)}
          emptyMessage="No ELF loans found."
        />
      </Grid>
    </Grid>
  );
};

export default ElfLoansList;
